/* eslint-disable max-lines */
import { NotificationTypeEnum } from 'configs/constants/enums/notification'
import debounce from 'lodash/debounce'
import escapeRegExp from 'lodash/escapeRegExp'
import set from 'lodash/set'
import trim from 'lodash/trim'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { getGroupsProcesses } from 'API/groupProcesses'
import { getNotifications } from 'API/notification'
import {
  aggregateCountProcessSteps,
  aggregateCountProcesses,
  countProcesses as countProcessesAPI,
  getProcessStepListAPI,
  getProcessById,
  getAllProcesses,
  aggregateProcessesSearch,
  getDetailProcessById
} from 'API/process'
import { updateStepBatch } from 'API/step'
import { ProcessList } from 'constants/process'
import { IProcess, IProcessWithRelations, IProcessesFilterForm } from 'interfaces/process'
import { IStep, IStepWithRelations } from 'interfaces/step'
import { getValidArray } from 'utils/common'

class ProcessStore {
  rootStore: RootStore

  process: IProcessWithRelations = {} as IProcessWithRelations
  stepsNeedUpdate: IStep[] = []

  processes: IProcessWithRelations[] = []

  processList: IProcessWithRelations[] = []

  processesFilter: IProcessesFilterForm = {
    collections: [],
    documentTypes: [],
    creators: [],
    groups: [],
    sort: ''
  }

  numberOfProcesses: number = 0

  numberOfDrafts: number = 0 // INFO: for action sheet render only

  selectedProcessId: number = -1

  selectedProcess: IProcessWithRelations = {} as IProcessWithRelations

  processDetail: IProcessWithRelations = {} as IProcessWithRelations

  openProcessDetailPanel: boolean = false

  openInformationModal: boolean = false

  openCommentModal: boolean = false

  expandingSteps: number[] = []

  isFetchingList: boolean = false

  timeFetchingList: number = new Date().getTime()

  isFetchingTotalNumber: boolean = false

  newProcessGroupIds: number[] = []

  isFetchingListSteps: boolean = false

  processSteps: IStep[] = []

  totalStepsProcess: number = 0

  isSearching: boolean | null = null

  isGroupProcessesLoading = false

  isManageModeInDetail = false
  canUserEditInProcessDetail = false

  isManageModeInArchive = false

  isManageModeInProcessList = false
  canUserEditInProcessList = false

  commonStepIdsNeedToUpdate: number[] = []

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public setManageModeInDetail(isManageMode: boolean) {
    this.isManageModeInDetail = isManageMode ?? false
  }

  public setManageModeInList(isManageMode: boolean) {
    this.isManageModeInProcessList = isManageMode ?? false
  }

  public setManageModeInArchive(isManageMode: boolean) {
    this.isManageModeInArchive = isManageMode ?? false
  }

  public setCanUserEditInProcessDetail(canEdit: boolean) {
    this.canUserEditInProcessDetail = canEdit
  }

  public setCanUserEditInProcessList(canEdit: boolean) {
    this.canUserEditInProcessList = canEdit
  }

  public setSelectedProcessId(processId: number): void {
    this.selectedProcessId = processId
  }

  public async setSelectedSharedGroupOfProcess(processId: number): Promise<void> {
    this.isGroupProcessesLoading = true
    const groupProcesses = await getGroupsProcesses({ where: { processId } })
    this.setNewProcessGroupIds(groupProcesses.map(groupProcess => groupProcess.groupId))
    this.isGroupProcessesLoading = false
  }

  public setSelectedProcess(process: IProcessWithRelations): void {
    this.selectedProcess = process
  }

  public async getProcessDetail(processId: number, filter: IFilter<IProcess> = {}): Promise<void> {
    const process = await getProcessById(processId, filter)
    this.process = process
    const notificationsOnStepUpdate = await getNotifications({
      where: { type: NotificationTypeEnum.UPDATED_COMMON_STEP_NOTIFICATION, processId }
    })
    this.commonStepIdsNeedToUpdate =
      getValidArray(notificationsOnStepUpdate).map(notification => notification.stepId) ?? []
    this.selectedProcess = process
    this.selectedProcessId = processId
  }

  public async setProcessStepPosition(
    temporarySteps: IStepWithRelations[],
    startIndex: number,
    endIndex: number
  ): Promise<void> {
    const [moved] = temporarySteps.splice(startIndex, 1)
    temporarySteps.splice(endIndex, 0, moved)
    const updatedSteps = temporarySteps.map((step, index) => ({ ...step, position: index + 1 }))
    await updateStepBatch(updatedSteps)
    this.process.steps = updatedSteps
  }

  public async getProcessByAggregate(
    dataQuery: AggregationPipeline = [],
    customParser: (response: unknown) => IProcessWithRelations[]
  ): Promise<void> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }
    this.isFetchingList = true
    const responseData = await aggregateProcessesSearch(dataQuery, currentTime)
    if (this.timeFetchingList === currentTime) {
      this.processes = customParser(responseData?.paginatedResults ?? [])
      this.numberOfProcesses = responseData?.totalResults ?? 0
    }
    this.isFetchingList = false
  }

  public resetProcessList(): void {
    this.processes = []
    this.numberOfProcesses = 0
  }

  public async countProcesses(where: Where<IProcess> = {}): Promise<void> {
    this.numberOfProcesses = await countProcessesAPI(where)
  }

  public async countDrafts(countQuery: AggregationPipeline = []): Promise<void> {
    const countResponse = await aggregateCountProcesses(countQuery)
    this.numberOfDrafts = countResponse
  }

  public setOpenInformationModal(status: boolean): void {
    this.openInformationModal = status
  }

  public setOpenCommentModal(status: boolean): void {
    this.openCommentModal = status
  }

  public setExpandingSteps(expandingSteps: number[]): void {
    this.expandingSteps = expandingSteps
  }

  public setOpenProcessDetailPanel(status: boolean): void {
    this.openProcessDetailPanel = status
  }

  public setProcessesFilter(processesFilter: IProcessesFilterForm): void {
    this.processesFilter = { ...this.processesFilter, ...processesFilter }
  }

  public setNewProcessGroupIds(newProcessGroupIds: number[]): void {
    this.newProcessGroupIds = newProcessGroupIds
  }

  public async getListStepsProcess(processId: number, offset: number, limit: number): Promise<void> {
    const responseData = await getProcessStepListAPI({
      limit: limit,
      offset: offset,
      where: {
        processId: processId
      },
      order: 'position ASC'
    })
    this.processSteps = responseData
  }

  public resetProcessSteps(): void {
    this.processSteps = []
  }

  public async getProcessDetailById(processId: number): Promise<IProcessWithRelations> {
    const processDetail = await getDetailProcessById(processId)
    this.processDetail = processDetail
    return processDetail
  }

  public async aggregateCountStepsProcess(processId: number): Promise<void> {
    const responseData = await aggregateCountProcessSteps({
      processId: processId
    })
    this.totalStepsProcess = responseData
  }

  public async getAllProcessList(organizationId: number, offset: number, keyword: string = ''): Promise<void> {
    const filter: IFilter<IProcess> = {
      where: {
        organizationId,
        name: {
          like: `.*${trim(keyword)}`,
          options: 'i'
        }
      },
      offset: offset,
      limit: ProcessList.LIMIT,
      order: ['updatedAt DESC'],
      include: [{ relation: 'documentType', scope: { include: [{ relation: 'iconBuilder' }] } }, { relation: 'steps' }]
    }
    const responseData = await getAllProcesses(filter)
    this.processList = [...this.processList, ...responseData]
  }

  public async search(
    keyword: string,
    offset: number,
    organizationId?: number,
    documentTypeIds?: number[],
    tagIds?: number[],
    creatorIds?: number[],
    limit?: number
  ): Promise<void> {
    const searchKeyword: string = escapeRegExp(trim(keyword))
    try {
      const filter = {
        offset,
        limit: limit ?? ProcessList.LIMIT,
        where: {
          isPublished: true,
          organizationId: organizationId ?? 0,
          name: {
            like: `.*${searchKeyword}`,
            options: 'i'
          },
          $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
        },
        order: ['updatedAt DESC'],
        include: [
          { relation: 'documentType', scope: { include: [{ relation: 'iconBuilder' }] } },
          { relation: 'steps' },
          { relation: 'tags' },
          { relation: 'creator' }
        ]
      }

      if (documentTypeIds?.length) {
        set(filter, 'where.documentTypeId', { inq: documentTypeIds })
      }

      if (creatorIds?.length) {
        set(filter, 'where.createdBy', { inq: creatorIds })
      }
      const responseData = await getAllProcesses(filter)
      if (tagIds?.length) {
        const filteredProcesses = responseData.filter(process =>
          process.tags?.some(tag => tagIds.includes(tag.id ?? 0))
        )
        this.processList = filteredProcesses
        return
      }

      this.processList = responseData
    } catch (error: any) {
      console.log('search Log ~ error', error)
    } finally {
      this.isSearching = false
    }
  }

  debouncedSearch = debounce(this.search, 700)

  public changeSearchText(searchText: string, offset?: number, organizationId?: number): void {
    this.isSearching = true
    this.debouncedSearch(searchText, offset ?? 0, organizationId)
  }
}

export default ProcessStore
